import React, { useState, useEffect, useMemo } from 'react'
import { StyledModal, StyledModalSection, StyledModalButton } from '@/components/ui/styled-modal'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Combobox } from '@headlessui/react'
import { Fragment } from 'react'
import { Transition } from '@headlessui/react'
import { Portal } from '@headlessui/react'
import { useSmartListsStore } from '@/store/smartListsStore'
import { useAuthStore } from '@/store/authStore'
import { useTransactionStore } from '@/store/transactionsStore'
import { useAccountsStore } from '@/store/accountsStore'
import { useCategoriesStore } from '@/store/categoriesStore'
import { usePayeesStore } from '@/store/payeesStore'
import { useTagsStore } from '@/store/tagsStore'
import { SmartListFilter, SmartListTemplate, SmartListCreationStep, SmartListPreview } from '@/types/smartList'
import { TEMPLATE_CATEGORIES, getPopularTemplates, getTemplatesByCategory } from './smartListTemplates'

import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import * as LucideIcons from 'lucide-react'
import { 
  ChevronRight, 
  ChevronLeft, 
  Sparkles, 
  Filter, 
  Eye, 
  Calendar, 
  DollarSign, 
  Search,
  TrendingUp,
  BarChart2,
  CheckCircle2,
  Tag,
  User,
  ChevronDown,
  Check,
  X,
  List,
  PieChart,
  CreditCard,
  PiggyBank,
  Receipt,
  Wallet,
  Target,
  Zap,
  Star,
  Heart,
  Home,
  Car,
  Plane,
  ShoppingBag,
  Coffee,
  Utensils,
  Gamepad2,
  Music,
  Book,
  Briefcase,
  GraduationCap,
  Users,
  Phone,
  Mail,
  MapPin,
  Camera
} from 'lucide-react'

interface EnhancedSmartListModalProps {
  isOpen: boolean
  onClose: () => void
}

const CREATION_STEPS: SmartListCreationStep[] = [
  {
    id: 'template',
    title: 'Choose Template',
    description: 'Start with a template or create from scratch',
    isComplete: false,
    isActive: true
  },
  {
    id: 'filters',
    title: 'Set Filters',
    description: 'Define which transactions to include',
    isComplete: false,
    isActive: false
  },
  {
    id: 'customize',
    title: 'Customize',
    description: 'Add name, icon, and color',
    isComplete: false,
    isActive: false
  },
  {
    id: 'preview',
    title: 'Preview & Save',
    description: 'Review and create your smart list',
    isComplete: false,
    isActive: false
  }
]

export function EnhancedSmartListModal({ isOpen, onClose }: EnhancedSmartListModalProps) {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { addSmartList } = useSmartListsStore()
  const { transactions } = useTransactionStore()
  const { accounts } = useAccountsStore()
  const { categories } = useCategoriesStore()
  const { payees } = usePayeesStore()
  const { tags } = useTagsStore()
  
  // Step management
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [steps, setSteps] = useState(CREATION_STEPS)
  
  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<SmartListTemplate | null>(null)
  const [filters, setFilters] = useState<SmartListFilter>({})
  const [name, setName] = useState('')
  const [icon, setIcon] = useState('Filter')
  const [color, setColor] = useState('#6366F1')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSelectedStartFromScratch, setHasSelectedStartFromScratch] = useState(false)

  // Template selection state
  const [selectedCategory, setSelectedCategory] = useState<string>('popular')
  
  // Combobox states
  const [payeeQuery, setPayeeQuery] = useState('')
  const [categoryQuery, setCategoryQuery] = useState('')
  const [isPayeeOpen, setIsPayeeOpen] = useState(false)
  const [isCategoryOpen, setIsCategoryOpen] = useState(false)
  
  // Preview data
  const preview = useMemo<SmartListPreview>(() => {
    // Calculate preview based on current filters
    const matchingTransactions = transactions.filter(transaction => {
      // Type filter
      if (filters.type && filters.type !== 'all' && transaction.type !== filters.type) return false
      
      // Search filter
      if (filters.search && !transaction.description?.toLowerCase().includes(filters.search.toLowerCase())) return false
      
      // Amount range filters
      if (filters.minAmount && transaction.amount < filters.minAmount) return false
      if (filters.maxAmount && transaction.amount > filters.maxAmount) return false
      
      // Category filter
      if (filters.category) {
        if (filters.category === 'none') {
          if (transaction.category_id) return false
        } else {
          if (transaction.category_id !== filters.category) return false
        }
      }
      
      // Payee filter
      if (filters.payee) {
        if (filters.payee === 'none') {
          if (transaction.payee_id || transaction.payee_name) return false
        } else {
          const payeeId = transaction.payee_id ? String(transaction.payee_id) : ''
          const payeeName = transaction.payee_name || ''
          if (payeeId !== filters.payee && payeeName !== filters.payee) return false
        }
      }
      
      return true
    })

    const totalAmount = matchingTransactions.reduce((sum, t) => sum + t.amount, 0)
    const dates = matchingTransactions.map(t => new Date(t.date)).filter(Boolean)
    
    return {
      transactionCount: matchingTransactions.length,
      totalAmount,
      dateRange: {
        earliest: dates.length > 0 ? new Date(Math.min(...dates.map(d => d.getTime()))) : null,
        latest: dates.length > 0 ? new Date(Math.max(...dates.map(d => d.getTime()))) : null
      },
      topCategories: []
    }
  }, [transactions, filters])

  // Update steps based on current progress
  useEffect(() => {
    setSteps(current => current.map((step, index) => ({
      ...step,
      isComplete: index < currentStepIndex,
      isActive: index === currentStepIndex
    })))
  }, [currentStepIndex])

  // Apply template when selected
  useEffect(() => {
    if (selectedTemplate) {
      setFilters(selectedTemplate.filters)
      setName(selectedTemplate.name)
      setIcon(selectedTemplate.icon)
      setColor(selectedTemplate.color)
    }
  }, [selectedTemplate])

  // Handle clicking outside to close comboboxes
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.combobox-container')) {
        setIsCategoryOpen(false);
        setIsPayeeOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [])

  const currentStep = steps[currentStepIndex]

  const canProceed = () => {
    switch (currentStep.id) {
      case 'template':
        return selectedTemplate !== null || hasSelectedStartFromScratch || Object.keys(filters).length > 0
      case 'filters':
        // Check if at least one meaningful filter is set
        const hasFilters = Boolean(
          filters.type && filters.type !== 'all' ||
          filters.dateRange && filters.dateRange !== 'all' ||
          filters.category ||
          filters.payee ||
          filters.minAmount ||
          filters.maxAmount ||
          filters.search
        )
        return hasFilters
      case 'customize':
        return name.trim().length > 0
      case 'preview':
        return true
      default:
        return false
    }
  }

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1)
    }
  }

  const handlePrevious = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1)
    }
  }

  const handleSubmit = async () => {
    if (!user?.id || !name.trim()) return

    setIsSubmitting(true)
    try {
      await addSmartList(user.id, name.trim(), filters, icon, color)
      onClose()
      // Reset form
      setCurrentStepIndex(0)
      setSelectedTemplate(null)
      setFilters({})
      setName('')
      setIcon('Filter')
      setColor('#6366F1')
      setHasSelectedStartFromScratch(false)
    } catch (error) {
      console.error('Error creating smart list:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderStepHeader = () => (
    <div className="mb-8">
      {/* Progress bar */}
      <div className="flex items-center justify-between mb-6">
        {steps.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div className={cn(
              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors",
              step.isComplete 
                ? "bg-[#439476] text-white" 
                : step.isActive 
                  ? "bg-[#439476]/20 text-[#439476] border-2 border-[#439476]"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-400"
            )}>
              {step.isComplete ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <span>{index + 1}</span>
              )}
            </div>
            {index < steps.length - 1 && (
              <div className={cn(
                "w-16 h-0.5 mx-2 transition-colors",
                step.isComplete ? "bg-[#439476]" : "bg-gray-200 dark:bg-gray-700"
              )} />
            )}
          </div>
        ))}
      </div>

      {/* Step title and description */}
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          {currentStep.title}
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          {currentStep.description}
        </p>
      </div>
    </div>
  )

  const renderTemplateStep = () => (
    <div className="space-y-6">
      {/* Category tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setSelectedCategory('popular')}
          className={cn(
            "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
            selectedCategory === 'popular'
              ? "bg-[#439476] text-white"
              : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
          )}
        >
          <Sparkles className="w-4 h-4 inline mr-2" />
          Popular
        </button>
        {TEMPLATE_CATEGORIES.map(category => {
          const IconComponent = (LucideIcons as any)[category.icon] || Filter
          return (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={cn(
                "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                selectedCategory === category.id
                  ? "bg-[#439476] text-white"
                  : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
            >
              <IconComponent className="w-4 h-4 inline mr-2" />
              {category.name}
            </button>
          )
        })}
      </div>

      {/* Template grid */}
      <ScrollArea className="h-80">
        <div className="grid grid-cols-2 gap-4">
          {(selectedCategory === 'popular' ? getPopularTemplates() : getTemplatesByCategory(selectedCategory)).map(template => {
            const IconComponent = (LucideIcons as any)[template.icon] || Filter
            return (
              <div
                key={template.id}
                onClick={() => {
                  setSelectedTemplate(template)
                  setHasSelectedStartFromScratch(false)
                }}
                className={cn(
                  "p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md",
                  selectedTemplate?.id === template.id
                    ? "border-[#439476] bg-[#439476]/5"
                    : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
                )}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: template.color + '20' }}
                  >
                    <IconComponent className="w-5 h-5" style={{ color: template.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                      {template.name}
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                      {template.description}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}

          {/* Custom option */}
          <div
            onClick={() => {
              setSelectedTemplate(null)
              setFilters({})
              setHasSelectedStartFromScratch(true)
            }}
            className={cn(
              "p-4 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md",
              hasSelectedStartFromScratch
                ? "border-[#439476] bg-[#439476]/5"
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            )}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <Filter className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 dark:text-white text-sm mb-1">
                  Start from Scratch
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Create your own custom filters
                </p>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  )

  const renderFiltersStep = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Transaction Type */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <DollarSign className="w-4 h-4 inline mr-2" />
            Transaction Type
          </label>
          <select
            value={filters.type || 'all'}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              type: e.target.value as any
            }))}
            className="w-full h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2"
          >
            <option value="all">All Types</option>
            <option value="income">Income</option>
            <option value="expense">Expense</option>
            <option value="transfer">Transfer</option>
          </select>
        </div>

        {/* Date Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Calendar className="w-4 h-4 inline mr-2" />
            Date Range
          </label>
          <select
            value={filters.dateRange || 'all'}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              dateRange: e.target.value as any
            }))}
            className="w-full h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-2"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="thisYear">This Year</option>
          </select>
        </div>

        {/* Amount Range */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Min Amount
          </label>
          <Input
            type="number"
            value={filters.minAmount || ''}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              minAmount: e.target.value ? Number(e.target.value) : undefined
            }))}
            placeholder="0.00"
            className="h-11"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Max Amount
          </label>
          <Input
            type="number"
            value={filters.maxAmount || ''}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              maxAmount: e.target.value ? Number(e.target.value) : undefined
            }))}
            placeholder="1000.00"
            className="h-11"
          />
        </div>

        {/* Categories */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Tag className="w-4 h-4 inline mr-2" />
            Categories
          </label>
          <Combobox
            as="div"
            value={filters.category || 'all'}
            onChange={(value) => {
              setFilters(prev => ({ 
                ...prev, 
                category: value === 'all' ? undefined : value
              }))
              setIsCategoryOpen(false)
            }}
            className="relative combobox-container"
          >
            <div className="relative">
              <div
                className="flex items-center h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-[#439476]/30 hover:border-[#439476] transition-all duration-200"
                onClick={() => setIsCategoryOpen(true)}
              >
                <Tag className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                <Combobox.Input
                  className="bg-transparent flex-1 outline-none border-none text-gray-900 dark:text-white placeholder:text-gray-500"
                  placeholder="Search categories..."
                  onChange={(e) => setCategoryQuery(e.target.value)}
                  displayValue={(categoryId: string) => {
                    if (!categoryId || categoryId === 'all') return '';
                    if (categoryId === 'none') return 'No Category';
                    
                    const category = categories.find(c => c.id === categoryId);
                    return category ? category.name : '';
                  }}
                />
                {categoryQuery && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCategoryQuery('');
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors mr-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {filters.category && filters.category !== 'all' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters(prev => ({ ...prev, category: undefined }));
                      setCategoryQuery('');
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <ChevronDown className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
              </div>
            </div>

            <Transition
              show={isCategoryOpen}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options
                static
                className="absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
              >
                <Combobox.Option
                  value="none"
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 ${
                      active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                        No Category
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                    </>
                  )}
                </Combobox.Option>
                <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                {categories
                  .filter(category => 
                    categoryQuery === '' || 
                    category.name.toLowerCase().includes(categoryQuery.toLowerCase())
                  )
                  .map((category) => (
                    <Combobox.Option
                      key={category.id}
                      value={category.id}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 px-4 ${
                          active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {category.name}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                              <Check className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                ))}
              </Combobox.Options>
            </Transition>
          </Combobox>
        </div>

        {/* Payees */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <User className="w-4 h-4 inline mr-2" />
            Payees
          </label>
          <Combobox
            as="div"
            value={filters.payee || 'all'}
            onChange={(value) => {
              setFilters(prev => ({ 
                ...prev, 
                payee: value === 'all' ? undefined : value
              }))
              setIsPayeeOpen(false)
            }}
            className="relative combobox-container"
          >
            <div className="relative">
              <div
                className="flex items-center h-11 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl px-4 cursor-pointer hover:ring-2 hover:ring-[#439476]/30 hover:border-[#439476] transition-all duration-200"
                onClick={() => setIsPayeeOpen(true)}
              >
                <User className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                <Combobox.Input
                  className="bg-transparent flex-1 outline-none border-none text-gray-900 dark:text-white placeholder:text-gray-500"
                  placeholder="Search payees..."
                  onChange={(e) => setPayeeQuery(e.target.value)}
                  displayValue={(payeeId: string) => {
                    if (!payeeId || payeeId === 'all') return '';
                    if (payeeId === 'none') return 'No Payee';
                    
                    const payee = payees.find(p => p.id === payeeId);
                    return payee ? payee.name : '';
                  }}
                />
                {payeeQuery && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPayeeQuery('');
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors mr-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                {filters.payee && filters.payee !== 'all' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilters(prev => ({ ...prev, payee: undefined }));
                      setPayeeQuery('');
                    }}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                <ChevronDown className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" />
              </div>
            </div>

            <Transition
              show={isPayeeOpen}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Combobox.Options
                static
                className="absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl mt-1 py-2 text-base shadow-lg max-h-60 overflow-auto focus:outline-none"
              >
                <Combobox.Option
                  value="none"
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-2 px-4 ${
                      active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                        No Payee
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                          <Check className="h-4 w-4" />
                        </span>
                      )}
                    </>
                  )}
                </Combobox.Option>
                <div className="border-t border-gray-200 dark:border-gray-600 my-1"></div>
                {payees
                  .filter(payee => 
                    payeeQuery === '' || 
                    payee.name.toLowerCase().includes(payeeQuery.toLowerCase())
                  )
                  .map((payee) => (
                    <Combobox.Option
                      key={payee.id}
                      value={payee.id}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 px-4 ${
                          active ? 'bg-[#439476]/10 text-[#439476]' : 'text-gray-900 dark:text-white'
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                            {payee.name}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#439476]">
                              <Check className="h-4 w-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Combobox.Option>
                ))}
              </Combobox.Options>
            </Transition>
          </Combobox>
        </div>

        {/* Search */}
        <div className="space-y-2 md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            <Search className="w-4 h-4 inline mr-2" />
            Search Term
          </label>
          <Input
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => ({ 
              ...prev, 
              search: e.target.value || undefined
            }))}
            placeholder="Search in descriptions, notes, payees..."
            className="h-11"
          />
        </div>
      </div>

      {/* Live preview */}
      <StyledModalSection
        title="Live Preview"
        icon={<Eye className="w-4 h-4" />}
        primaryColor="#439476"
        className="mt-6"
      >
        <p className="text-sm text-[#439476] dark:text-[#439476]">
          <strong>{preview.transactionCount}</strong> transactions found
          {preview.totalAmount !== 0 && (
            <span> • Total: <strong>${Math.abs(preview.totalAmount).toFixed(2)}</strong></span>
          )}
        </p>
      </StyledModalSection>
    </div>
  )

  const renderCustomizeStep = () => (
    <div className="space-y-6">
      <div className="space-y-4">
        {/* Name */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Smart List Name
          </label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Enter a name for your smart list"
            className="h-11"
          />
        </div>

        {/* Icon and Color */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Icon
            </label>
            <div className="grid grid-cols-8 gap-2">
              {[
                'Filter', 'List', 'BarChart2', 'PieChart', 'TrendingUp', 'DollarSign', 'Calendar', 'Tag',
                'CreditCard', 'PiggyBank', 'Receipt', 'Wallet', 'Target', 'Zap', 'Star', 'Heart',
                'Home', 'Car', 'Plane', 'ShoppingBag', 'Coffee', 'Utensils', 'Gamepad2', 'Music',
                'Book', 'Briefcase', 'GraduationCap', 'Users', 'Phone', 'Mail', 'MapPin', 'Camera'
              ].map(iconName => {
                const IconComponent = (LucideIcons as any)[iconName]
                if (!IconComponent) return null
                return (
                  <button
                    key={iconName}
                    onClick={() => setIcon(iconName)}
                    className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                      icon === iconName
                        ? "bg-[#439476] text-white"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                    )}
                  >
                    <IconComponent className="w-4 h-4" />
                  </button>
                )
              })}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Color
            </label>
            <div className="grid grid-cols-6 gap-2">
              {[
                '#439476', '#6366F1', '#8B5CF6', '#EC4899', '#EF4444', '#F59E0B',
                '#10B981', '#06B6D4', '#84CC16', '#F97316', '#EAB308', '#6B7280'
              ].map(colorOption => (
                <button
                  key={colorOption}
                  onClick={() => setColor(colorOption)}
                  className={cn(
                    "w-8 h-8 rounded-lg border-2 transition-all",
                    color === colorOption
                      ? "border-gray-400 scale-110"
                      : "border-gray-200 dark:border-gray-600 hover:scale-105"
                  )}
                  style={{ backgroundColor: colorOption }}
                />
              ))}
            </div>
            <Input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-full h-8"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Preview</h4>
          <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-700 rounded-lg">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: color + '20' }}
            >
              {(() => {
                const IconComponent = (LucideIcons as any)[icon] || Filter
                return <IconComponent className="w-4 h-4" style={{ color }} />
              })()}
            </div>
            <span className="font-medium text-gray-900 dark:text-white">
              {name || 'Untitled Smart List'}
            </span>
          </div>
        </div>
      </div>
    </div>
  )

  const renderPreviewStep = () => (
    <div className="space-y-6">
      {/* Smart List Summary */}
      <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-xl">
        <div className="flex items-center gap-4 mb-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: color + '20' }}
          >
            {(() => {
              const IconComponent = (LucideIcons as any)[icon] || Filter
              return <IconComponent className="w-6 h-6" style={{ color }} />
            })()}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              {name}
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Smart List • {preview.transactionCount} transactions
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <BarChart2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Transactions</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {preview.transactionCount}
            </p>
          </div>
          
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm text-gray-600 dark:text-gray-400">Total Amount</span>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              ${Math.abs(preview.totalAmount).toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Applied Filters */}
      <div className="space-y-3">
        <h4 className="font-medium text-gray-900 dark:text-white">Applied Filters</h4>
        <div className="flex flex-wrap gap-2">
          {Object.entries(filters).map(([key, value]) => {
            if (!value) return null
            return (
              <Badge 
                key={key} 
                variant="secondary"
                className="bg-[#439476]/10 text-[#439476] border-[#439476]/20"
              >
                {key}: {String(value)}
              </Badge>
            )
          })}
          {Object.keys(filters).length === 0 && (
            <Badge variant="outline" className="text-gray-500">
              No filters applied
            </Badge>
          )}
        </div>
      </div>
    </div>
  )

  const renderStepContent = () => {
    switch (currentStep.id) {
      case 'template':
        return renderTemplateStep()
      case 'filters':
        return renderFiltersStep()
      case 'customize':
        return renderCustomizeStep()
      case 'preview':
        return renderPreviewStep()
      default:
        return null
    }
  }

  return (
    <StyledModal
      isOpen={isOpen}
      onClose={onClose}
      title="Create Smart List"
      maxWidth="900px"
      footer={
        <div className="flex items-center justify-between w-full">
          <StyledModalButton
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStepIndex === 0}
            icon={<ChevronLeft className="w-4 h-4" />}
          >
            Previous
          </StyledModalButton>

          <div className="flex items-center gap-3">
            <StyledModalButton
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </StyledModalButton>

            {currentStepIndex < steps.length - 1 ? (
              <StyledModalButton
                variant="primary"
                onClick={handleNext}
                disabled={!canProceed()}
                icon={<ChevronRight className="w-4 h-4" />}
              >
                Next
              </StyledModalButton>
            ) : (
              <StyledModalButton
                variant="success"
                onClick={handleSubmit}
                disabled={!canProceed() || isSubmitting}
                icon={<CheckCircle2 className="w-4 h-4" />}
              >
                {isSubmitting ? 'Creating...' : 'Create Smart List'}
              </StyledModalButton>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {renderStepHeader()}
        <div className="min-h-[400px]">
          {renderStepContent()}
        </div>
      </div>
    </StyledModal>
  )
} 